import * as THREE from 'three';
import { coreGlow } from '../render/materials';
import { PROP } from './mech-kit';
import { IK_C0, POSE, STANCE } from './mech-pose';
import { buildCore } from './mech-core';
import { buildCockpit, buildPylon, buildSensor } from './mech-parts';
import { buildMain } from './mech-arm';
import { buildBackUnit, buildThruster } from './mech-legs';
import { equippedShape } from './mech-shape';
import { mergeBones } from './mech-merge';
import { buildLancer2 } from './lancer';
import { buildTitan3 } from './titan';
import { newAnim } from './mech-anim';
/* ============================================================
   機體組裝（1:1 移植 `mech_sample_ani.html` §6 / §6b）
   ============================================================

   三層骨架。**這是這一版最重要的結構改動**：

     root
      └ body        垂直：彈跳 / 蹲下 / 落地壓縮（上下半身一起動）
         ├ lower    rotation.y = 移動方向  → 骨盆 + 兩腿
         └ torso    rotation.y = 瞄準方向  → 核心 + 背包 + 頭 + 掛架 + 右臂

   扭腰 = `torso.rotation.y − lower.rotation.y`，不需另外計算。
   遊戲的瞄準是全自動的（砲塔追鎖定目標），所以扭腰是免費拿到的 ——
   玩家往一邊跑、砲口指另一邊，那個交叉就是「機甲感」的來源。

   舊版是兩層 `root > { lower, upper }`，垂直位移靠 `rig.waistY` 這個補丁
   （兩台機體的約定還不一樣，害過一次「上下半身分離」）。
   新版垂直一律歸 `body` 管，**`waistY` 已經拿掉，不要再加回來**。 */
export function buildChassis0(loadout?) {
  var s = loadout || {
    core: equippedShape('core', 'core-std', 'chassis0'),
    cockpit: equippedShape('cockpit', 'cock-std', 'chassis0'),
    thruster: equippedShape('thruster', 'thr-twin', 'chassis0'),
    main: equippedShape('main', 'main-vulcan', 'chassis0'),
    sensor: equippedShape('sensor', 'sen-std', 'chassis0'),
    pylon: equippedShape('pylon', 'pyl-std', 'chassis0')
  };
  var P = { glow: coreGlow(s.core) };          // 核心決定全機發光色相
  var root = new THREE.Group();
  var parts: any = {};

  var body = new THREE.Group(); (body.userData as any).bone = 1;
  body.position.y = STANCE.bodyY;
  root.add(body);

  var lower = buildThruster(s.thruster, P);
  (lower.userData as any).bone = 1;
  lower.rotation.y = POSE.hipYaw;
  parts.thruster = lower; body.add(lower);

  var torso = buildCore(s.core, P);
  (torso.userData as any).bone = 1;
  torso.position.y = 2.22 - STANCE.bodyY;
  torso.rotation.set(POSE.torsoPitch, 0, POSE.torsoRoll);
  parts.core = torso; body.add(torso);

  var back = buildBackUnit(s.thruster, P);     // 與下半身同一個 shape 代號
  (back.userData as any).bone = 1;
  back.position.set(0, 0.72, -0.58);
  parts.back = back; torso.add(back);

  var head = buildCockpit(s.cockpit, P);
  (head.userData as any).bone = 1;
  head.position.set(0.14, 1.06, 0.02);         // 偏左，破除鏡像
  head.rotation.set(POSE.headPitch, POSE.headYaw, -POSE.torsoRoll * 0.7);
  parts.cockpit = head; torso.add(head);

  var sen = buildSensor(s.sensor, P);          // 感應器與頭同剛體，會併進 head
  sen.position.set(-0.26, 0.24, 0.03);
  parts.sensor = sen; head.add(sen);

  var pyl = buildPylon(s.pylon, P);
  (pyl.userData as any).bone = 1;
  pyl.position.set(1.02, 0.94, 0);
  pyl.rotation.set(0, POSE.pylonYaw, POSE.pylonRoll);
  parts.pylon = pyl; torso.add(pyl);

  var arm = buildMain(s.main, P);
  (arm.userData as any).bone = 1;
  arm.position.set(-1.02, 0.94, 0);
  parts.main = arm; torso.add(arm);

  var rig: any = {
    /* `pose`/`stance`/`ik` 三組常數是**機體之間唯一的差異來源**：動畫層
       （`mech-anim.ts`）一律讀 rig 上的這三個，不讀模組層級的 POSE/STANCE，
       所以加第三台機體不用再動動畫層。 */
    kind: 'v3', root: root, parts: parts, shapes: s,
    pose: POSE, stance: STANCE, ik: IK_C0,
    body: body, lower: lower, torso: torso, back: back, head: head, pylon: pyl,
    pelvis: (lower.userData as any).rig.pelvis,
    legs: (lower.userData as any).rig.legs,
    arm: (arm.userData as any).rig,
    muzzleY: (arm.userData as any).muzzle,
    nozzles: (back.userData as any).nozzles,
    vents: (back.userData as any).vents,
    glow: P.glow,
    /* 舊介面的殘骸。`sim/projectiles.ts` 會掃 `muzzles` 收槍口閃光，
       新版的閃光改由 `render/mech-fx.ts` 管，所以這裡留一個空陣列
       讓那個迴圈變成無害的空操作，不用去改呼叫端。 */
    muzzles: [], jets: []
  };
  (root.userData as any).rig = rig;

  /* **合併一定要在建立特效之前**（特效由 `render/mech-fx.ts` 在
     `equipMech` 之後掛上去）。噴焰與槍口閃光是每幀改 scale/opacity 的
     動態物件，被烘進靜態幾何就再也動不了，而且不會有任何錯誤。 */
  mergeBones(root);
  rig.anim = newAnim();
  root.scale.setScalar(PROP.scale);            // 唯一的縮放：放到遊戲世界的尺度
  return rig;
}

export var MECHS = {
  chassis0: {
    id: 'chassis0', name: 'CHASSIS-0', cn: '零號構裝', desc: '均衡射擊型。中距離消耗，靠飛彈與掃射光束控場。',
    /* 推進器：重裝這台**燃料多但爬得慢**（120 點 ≈ 3.5 秒滯空、上升 8.0/秒 ≈
       1.5 秒到頂）。輕型的是反過來，兩台的差異軸與 speed 同一路。
       ⚠️ `dashMax`（衝刺格數）2026-08-06 移除：衝刺改吃推進能量，兩者共用一條。 */
    hp: 100, sh: 40, speed: 11.5, thrustMax: 120, thrustRise: 8.0, build: buildChassis0,
    primary: 'vulcan', mid: 'missile', ult: 'beam', icons: ['機炮', '飛彈', '光束'],
    /* 主砲 2026-08-05（使用者拍板）：**發彈頻率減半、單發傷害加倍、只從右手射出**。
       7.5 發/秒 9 傷 → 3.75 發/秒 18 傷。DPS 不變，改的是**手感**：
       從「連續的細碎彈流」變成「一發一發打出去」，每一發看得到、聽得到。
       ⚠️ 這同時放大了每一發的價值 —— 暴擊（`crit`）與彈數（`atkCount`）
       這兩條軸的體感會跟著變強，它們現在動的是更大的一塊。 */
    /* 大招（光束）2026-08-06 使用者要求拉高：**30 → 54**（+80%）。
       光束每 0.07 秒一 tick、每 tick 打一份 `ultDmg`（`sim/weapons.ts`），
       所以這個數字的意義是「掃過去的那一瞬間能不能帶走一隻」——
       30 的時候連最小的 nadel（開場 36 HP）都差一點，掃過去要停兩拍。
       54 之後：nadel 一拍、lanze(87) 兩拍、geissel(108) 兩拍。
       ⚠️ 20 秒冷卻的招式，總傷害是 54 × 33 拍 ≈ 1,780（對站著不動的單體）。
       調它要看 `pnpm metrics` 第 5 區：單一來源佔比超過 40% 就是一枝獨秀。 */
    stats: { atkDmg: 18, atkRate: 3.75, atkRange: 30, atkCount: 1, atkSpeed: 62, midDmg: 34, midCount: 4, midCdMax: 5.2, ultDmg: 54, ultCdMax: 20, ultDur: 2.3 }
  },
  lancer2: {
    id: 'lancer2', name: 'LANCER-2', cn: '斬型', desc: '高速近戰型。結構較脆但推進回充快，貼身時輸出遠高於射擊型。',
    hp: 82, sh: 30, speed: 13.6, thrustMax: 90, thrustRise: 10.5, build: buildLancer2,
    primary: 'blade', mid: 'dashSlash', ult: 'vortex', icons: ['電磁刃', '突進斬', '旋刃'],
    /* 大招（旋刃）跟著拉高：**20 → 36**（同樣 +80%，維持兩台的相對關係）。
       旋刃的結算式是 `ultDmg × (2 + 3×spin)`（`sim/weapons.ts`），
       所以它的單次收束傷害從 40–100 變成 72–180。 */
    stats: { atkDmg: 30, atkRate: 2.7, atkRange: 7.6, atkCount: 1, atkSpeed: 0, midDmg: 62, midCount: 0, midCdMax: 4.0, ultDmg: 36, ultCdMax: 18, ultDur: 4.0 }
  },
  titan3: {
    id: 'titan3', name: 'TITAN-3', cn: '壯漢型', desc: '重砲支援型。移動最慢、結構最厚，三把武裝全是範圍爆炸 —— 打的是一塊地不是一個人。',
    /* 三台的差異軸：CHASSIS-0 均衡、LANCER-2 快而脆、TITAN-3 慢而硬。
       推進器沿著「燃料多、爬得慢」那一端再往外一階（120/8.0 → 150/6.2）：
       這台飛得起來但飛得笨重，滯空是用來換位置不是用來閃。 */
    hp: 138, sh: 55, speed: 9.4, thrustMax: 150, thrustRise: 6.2, build: buildTitan3,
    primary: 'mortar', mid: 'rocket', ult: 'ringBurst', icons: ['榴彈砲', '火箭砲', '分裂彈'],
    /* 普攻（榴彈砲）：1.45 發/秒 × 32 傷 ≈ 46 DPS，是 CHASSIS-0（67）的
       七成 —— 但每一發是半徑 3.6 的爆炸，打成群的雜魚時總量遠高於它。
       **代價寫在彈道裡**：拋物線飛 0.4–1.1 秒才落地，前置量只給七成，
       所以打單一移動目標會落空。這是「範圍換命中率」，不是純粹的加強。
       `atkSpeed` 在這把武裝上的語意是彈道水平速度（見 `sim/weapons-titan.ts`）。

       小招（火箭砲）：一發 145 傷、半徑 6.0 的追蹤火箭，6 秒冷卻。
       CHASSIS-0 是「四發 34 傷分散」，這台把同一份預算壓在一顆上 ——
       清雜魚較差、打精英與 Boss 較好，與「重砲」的身份一致。

       大招（分裂彈）：三波環形榴彈（7/8/9 顆，半徑 6.5→11.5→16.5），
       每顆落地爆炸再分裂兩顆小彈。單顆 42 傷，理論總量約 24×42 + 分裂
       ≈ 1,450（打在人堆裡才拿得到，站空地上只會炸出一圈煙）——
       與 CHASSIS-0 的光束（≈1,780 對站著不動的單體）走的是相反的軸。
       ⚠️ `ultDur 1.4` 不是持續時間而是**收尾等待**：最後一波在 0.8 秒發射，
       砲彈還要飛 0.6 秒才落地，冷卻圖示不能比爆炸早結束。 */
    stats: { atkDmg: 32, atkRate: 1.45, atkRange: 28, atkCount: 1, atkSpeed: 30, midDmg: 145, midCount: 1, midCdMax: 6.0, ultDmg: 42, ultCdMax: 19, ultDur: 1.4 }
  }
};
